import { useRef, useEffect, useState, useCallback } from 'react';

// 스토리지 키 접두사 정의
const STORAGE_KEYS = {
    GROUPING_PREFIX: 'grouping_',
};

/**
 * 그룹핑 옵션 관리를 위한 훅 인터페이스
 */
export interface GroupingInterface<T extends string> {
    selectedGrouping: T;
    setSelectedGrouping: (value: T) => void;
    resetGrouping: () => void;
    isLoading: boolean;
}

/**
 * 그룹핑 설정을 로컬 스토리지에 저장하고 불러오는 훅
 * @param id 리소스 ID (팀 ID 또는 다른 식별자)
 * @param defaultGrouping 기본 그룹핑 값
 * @param prefix 스토리지 키 접두사 (기본값: GROUPING_PREFIX)
 */
export function useGroupingStorage<T extends string>(
    id: string | undefined,
    defaultGrouping: T,
    prefix: string = STORAGE_KEYS.GROUPING_PREFIX
): GroupingInterface<T> {
    // 선택된 그룹핑 옵션 상태
    const [selectedGrouping, setSelectedGrouping] =
        useState<T>(defaultGrouping);
    const [isLoading, setIsLoading] = useState(false);

    // 이전 ID와 초기 로드 상태를 ref로 관리 (렌더링 중 상태 변경 방지)
    const previousIdRef = useRef<string | undefined>(id);
    const initialLoadDoneRef = useRef<boolean>(false);
    const isUpdatingRef = useRef<boolean>(false);

    // id, prefix 값으로 스토리지 키 생성 함수 (useCallback으로 메모이제이션)
    const getStorageKey = useCallback(
        (resourceId: string | undefined) => {
            return resourceId ? `${prefix}${resourceId}` : null;
        },
        [prefix]
    );

    // 로컬 스토리지에서 값 로드 함수 (동기적 처리)
    const loadFromStorage = useCallback(
        (key: string | null): T => {
            if (!key) return defaultGrouping;

            // 이미 로딩 중이면 기다리지 않고 기본값 반환
            if (isUpdatingRef.current) {
                return defaultGrouping;
            }

            try {
                setIsLoading(true);
                isUpdatingRef.current = true;

                const savedValue = localStorage.getItem(key);
                const result = savedValue
                    ? (JSON.parse(savedValue) as T)
                    : defaultGrouping;

                return result;
            } catch (error) {
                console.error(
                    `로컬 스토리지에서 "${key}" 값을 파싱하는 중 오류가 발생했습니다:`,
                    error
                );
                return defaultGrouping;
            } finally {
                setIsLoading(false);
                isUpdatingRef.current = false;
            }
        },
        [defaultGrouping]
    );

    // 로컬 스토리지에 값 저장 함수 (동기적 처리)
    const saveToStorage = useCallback((key: string | null, value: T) => {
        if (!key || isUpdatingRef.current) return;

        try {
            isUpdatingRef.current = true;
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.error(
                `로컬 스토리지에 "${key}" 값을 저장하는 중 오류가 발생했습니다:`,
                error
            );
        } finally {
            isUpdatingRef.current = false;
        }
    }, []);

    // useEffect에서 동기적 처리
    useEffect(() => {
        const currentId = id;
        const storageKey = getStorageKey(currentId);

        if (
            currentId !== previousIdRef.current ||
            !initialLoadDoneRef.current
        ) {
            if (currentId) {
                // 동기적 로드
                const loadedValue = loadFromStorage(storageKey);
                if (currentId === id) {
                    setSelectedGrouping(loadedValue);
                    previousIdRef.current = currentId;
                    initialLoadDoneRef.current = true;
                }
            } else {
                setSelectedGrouping(defaultGrouping);
                previousIdRef.current = currentId;
                initialLoadDoneRef.current = true;
            }
        }
    }, [id, getStorageKey, loadFromStorage, defaultGrouping]);

    // 상태 변경 시 저장 (디바운스 적용)
    useEffect(() => {
        if (initialLoadDoneRef.current && id && id === previousIdRef.current) {
            const storageKey = getStorageKey(id);

            // 디바운스 적용 (빠른 연속 변경 방지)
            const timeoutId = setTimeout(() => {
                saveToStorage(storageKey, selectedGrouping);
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [selectedGrouping, id, getStorageKey, saveToStorage]);

    // 그룹핑 리셋 함수
    const resetGrouping = useCallback(() => {
        setSelectedGrouping(defaultGrouping);
        if (id) {
            const storageKey = getStorageKey(id);
            if (storageKey) {
                localStorage.removeItem(storageKey);
            }
        }
    }, [defaultGrouping, id, getStorageKey]);

    return {
        selectedGrouping,
        setSelectedGrouping,
        resetGrouping,
        isLoading,
    };
}
